import { HttpStatus } from './http-status.util';
import { normalizeApiErrors } from './api-errors.util';
import { SUPERADMIN_AUDIT_UNAVAILABLE_MESSAGE } from './superadmin-audit-unavailable.util';

/**
 * Failure codes `POST /api/user/change-password` sends in `errorCode`.
 *
 * `errorCode` is the contract; the `errors` prose is not (it varies by
 * environment), so every branch below keys on the code. The 400s all carry
 * `errors` as a list; the 429 is the odd one out (see `RateLimited`).
 */
export enum ChangePasswordErrorCode {
  /** 400: the typed current password is wrong. Nothing was changed. */
  CurrentPasswordIncorrect = 'CURRENT_PASSWORD_INCORRECT',
  /**
   * 400: a field is empty, the new password breaks the policy, or the
   * confirmation differs. SEVERAL messages can arrive at once — show them all.
   */
  PasswordValidationFailed = 'PASSWORD_VALIDATION_FAILED',
  /** 400: the policy passed but the identity store refused it (store/concurrency failure). */
  NewPasswordRejected = 'NEW_PASSWORD_REJECTED',
  /**
   * 429: over the 20-per-minute budget this endpoint SHARES with
   * `verify-current-password`. The body has NO `data` key and is not an
   * `ApiResponse` envelope, and there is no `Retry-After` — hence the fixed
   * cooldown below.
   */
  RateLimited = 'PASSWORD_RATE_LIMITED',
}

/*
 * Not a code: a SuperAdmin caller can also get a 503 with no `errorCode` when
 * the server's SuperAdmin audit trail is switched off (BE note, 2026-10-20).
 * The password is NOT changed. Recognised by status alone; see the `unavailable`
 * branch of `resolveChangePasswordFailure`.
 */

/**
 * How long My Profile pauses the password save after a rate limit. The server
 * sends no `Retry-After`; its budget is per minute, so a minute is the honest
 * wait.
 */
export const CHANGE_PASSWORD_COOLDOWN_MS = 60_000;

export const CHANGE_PASSWORD_RATE_LIMITED_MESSAGE =
  'Too many attempts. Please try again in a minute.';
export const CHANGE_PASSWORD_GENERIC_FAILURE_MESSAGE =
  'Failed to change your password. Please try again later.';
const CURRENT_PASSWORD_INCORRECT_FALLBACK_MESSAGE = 'Current password is incorrect';
const PASSWORD_VALIDATION_FALLBACK_MESSAGE =
  'Your new password does not meet the password requirements.';
const NEW_PASSWORD_REJECTED_FALLBACK_MESSAGE =
  'Your new password could not be accepted. Please choose a different one.';

export type ChangePasswordFailureKind =
  | 'incorrectCurrent'
  | 'validation'
  | 'rejected'
  | 'rateLimited'
  | 'unavailable'
  | 'generic';

/** Which password box a failure is about, when one box is clearly at fault. */
export type ChangePasswordFieldHint = 'currentPassword' | 'newPassword';

export interface ChangePasswordFailure {
  kind: ChangePasswordFailureKind;
  /** One entry per message to show; never empty. */
  messages: string[];
  fieldHint?: ChangePasswordFieldHint;
}

/** The slice of a failed response this resolver reads (an `ApiResponse` satisfies it). */
export interface ChangePasswordFailureSource {
  statusCode?: number;
  errors?: unknown;
  errorCode?: string;
}

/**
 * Turns a failed change-password response into what the page should say.
 *
 * Pure and DI-free. A 429 is recognised by code OR by status, because the
 * code only appears for this one rate-limit policy but the status alone is
 * enough to know "slow down".
 *
 * An unrecognised code (or none) keeps today's behaviour: the server's first
 * message when it sent one — e.g. "User has pending changes" — else a generic
 * sentence.
 */
export function resolveChangePasswordFailure(
  response: ChangePasswordFailureSource | null | undefined
): ChangePasswordFailure {
  const serverMessages = normalizeApiErrors(response?.errors);
  const messagesOr = (fallback: string): string[] =>
    serverMessages.length > 0 ? serverMessages : [fallback];

  if (
    response?.errorCode === ChangePasswordErrorCode.RateLimited ||
    response?.statusCode === HttpStatus.TooManyRequests
  ) {
    return { kind: 'rateLimited', messages: [CHANGE_PASSWORD_RATE_LIMITED_MESSAGE] };
  }

  // Server configuration, not user error: our own copy (never the server's
  // audit wording), and no cooldown, since waiting a minute will not fix it.
  if (response?.statusCode === HttpStatus.ServiceUnavailable) {
    return { kind: 'unavailable', messages: [SUPERADMIN_AUDIT_UNAVAILABLE_MESSAGE] };
  }

  switch (response?.errorCode) {
    case ChangePasswordErrorCode.CurrentPasswordIncorrect:
      return {
        kind: 'incorrectCurrent',
        messages: messagesOr(CURRENT_PASSWORD_INCORRECT_FALLBACK_MESSAGE),
        fieldHint: 'currentPassword',
      };
    case ChangePasswordErrorCode.PasswordValidationFailed:
      return {
        kind: 'validation',
        messages: messagesOr(PASSWORD_VALIDATION_FALLBACK_MESSAGE),
        fieldHint: 'newPassword',
      };
    case ChangePasswordErrorCode.NewPasswordRejected:
      return {
        kind: 'rejected',
        messages: messagesOr(NEW_PASSWORD_REJECTED_FALLBACK_MESSAGE),
        fieldHint: 'newPassword',
      };
    default:
      return {
        kind: 'generic',
        messages: [serverMessages[0] ?? CHANGE_PASSWORD_GENERIC_FAILURE_MESSAGE],
      };
  }
}
